import { Controller } from "@hotwired/stimulus"

// Toggles visibility of a section based on another field's value.
// Usage:
// <div
//   data-controller="conditional-visibility"
//   data-conditional-visibility-field-selector-value="input[name='form[response_type]']"
//   data-conditional-visibility-match-value-value="select">
//   <!-- Section to show/hide -->
// </div>
// With multiple match values (comma-separated):
// <div
//   data-controller="conditional-visibility"
//   data-conditional-visibility-field-selector-value="input[name='form[type]']"
//   data-conditional-visibility-match-values-value="a,b,c">
//   <!-- Section to show/hide -->
// </div>
// With inverse option (show when value does NOT match):
// <div
//   data-controller="conditional-visibility"
//   data-conditional-visibility-field-selector-value="input[name='form[response_type]']"
//   data-conditional-visibility-match-value-value="select"
//   data-conditional-visibility-inverse-value="true">
//   <!-- Section to show/hide -->
// </div>
export default class extends Controller {
  static values = {
    fieldSelector: String,
    matchValue: String,
    matchValues: String,
    hideClass: { type: String, default: "hidden" },
    inverse: { type: Boolean, default: false },
  }

  connect() {
    this.boundEvaluate = this.evaluate.bind(this)
    this.attachFieldListener()
    this.evaluate()
  }

  disconnect() {
    this.detachFieldListener()
  }

  fieldSelectorValueChanged() {
    this.detachFieldListener()
    this.attachFieldListener()
    this.evaluate()
  }

  evaluate() {
    if (!this.field || (!this.hasMatchValueValue && !this.hasMatchValuesValue)) {
      this.element.classList.remove(this.hideClassValue)
      return
    }

    const fieldValue =
      this.field.type === "checkbox"
        ? String(this.field.checked)
        : this.field.value
    const matches = this.hasMatchValuesValue
      ? this.matchValuesValue.split(",").includes(fieldValue)
      : fieldValue === this.matchValueValue
    const shouldShow = this.inverseValue ? !matches : matches
    this.element.classList.toggle(this.hideClassValue, !shouldShow)
  }

  attachFieldListener() {
    this.field = this.findField()

    if (this.field) {
      this.field.addEventListener("change", this.boundEvaluate)
      this.field.addEventListener("input", this.boundEvaluate)

      // HotwireCombobox dispatches selection events on the fieldset ancestor
      this.comboboxFieldset = this.field.closest("fieldset.hw-combobox")
      if (this.comboboxFieldset) {
        this.comboboxFieldset.addEventListener("hw-combobox:selection", this.boundEvaluate)
      }
    }
  }

  detachFieldListener() {
    if (this.field && this.boundEvaluate) {
      this.field.removeEventListener("change", this.boundEvaluate)
      this.field.removeEventListener("input", this.boundEvaluate)
    }

    if (this.comboboxFieldset && this.boundEvaluate) {
      this.comboboxFieldset.removeEventListener("hw-combobox:selection", this.boundEvaluate)
      this.comboboxFieldset = null
    }
  }

  findField() {
    if (!this.hasFieldSelectorValue) return null

    const root = this.element.closest("form") || document
    return root.querySelector(this.fieldSelectorValue)
  }
}

